/*
 * ============================================================================
 * DATOS FICTICIOS — escenario de pruebas. Los agentes, departamentos,
 * usuarios y tenants de este simulador son inventados y NO describen el
 * producto ni lo desplegado. Solo se usa en desarrollo y en las pruebas del
 * Pórtico; en producción está apagado.
 * ============================================================================
 */

/**
 * Mock de la gestión de usuarios/roles del tenant — US-26.
 * Ref: contrato admin PROPUESTO en US-26 (endpoints /v1/admin/users), pendiente
 * de VoBo de Cassie y de los endpoints reales del kernel — aún no es acuerdo
 * cerrado.
 *
 * Se usa mientras los endpoints reales del kernel no estén desplegados. Cuando
 * lo estén, src/api/client.js conmuta a las llamadas reales (VITE_USE_MOCK=false)
 * y este módulo deja de intervenir. La lógica de validación vive aquí (módulo
 * puro, sin React) para poder probarla de forma unitaria y para que el mock
 * reproduzca los mismos códigos de error que el backend.
 */

import { ROLES } from '../lib/contract.js'
import { ApiError } from '../api/errors.js'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Empleados semilla del tenant DKtec (mock). El usuario de la sesión
// (user-nombre-001, admin) — permite validar el guardarrail de auto-degradación.
const SEED = [
  { id: 'user-nombre-001', display_name: 'Nombre Apellido', email: 'nombre@ejemplo.test', rol: 'admin',    estado: 'activo'    },
  { id: 'u-ana',         display_name: 'Ana Torres',   email: 'ana.torres@dktec.es',   rol: 'champion', estado: 'activo'    },
  { id: 'u-luis',        display_name: 'Luis Sáez',    email: 'luis.saez@dktec.es',    rol: 'usuario',  estado: 'activo'    },
  { id: 'u-marta',       display_name: 'Marta Ruiz',   email: 'marta.ruiz@dktec.es',   rol: 'usuario',  estado: 'pendiente' },
  { id: 'u-pablo',       display_name: 'Pablo Gil',    email: 'pablo.gil@dktec.es',    rol: 'usuario',  estado: 'baja'      },
]

let _users = SEED.map(u => ({ ...u }))
let _seq = 0

// Deriva un nombre presentable del email hasta que el usuario complete su alta.
function displayNameFromEmail(email) {
  const local = String(email).split('@')[0] ?? ''
  const nombre = local
    .split(/[.\-_]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
  return nombre || email
}

export function listMockUsers() {
  return _users.map(u => ({ ...u }))
}

export function inviteMockUser({ email, rol, nombre }) {
  if (!EMAIL_RE.test(String(email ?? '').trim())) {
    throw new ApiError('invalid_email', 'El email no tiene un formato válido.', 422)
  }
  if (!ROLES.includes(rol)) {
    throw new ApiError('invalid_role', 'El rol indicado no es válido.', 422)
  }
  const normalized = email.trim()
  if (_users.some(u => u.email.toLowerCase() === normalized.toLowerCase())) {
    throw new ApiError('email_already_exists', 'Ya existe un usuario con ese email en el tenant.', 409)
  }
  const user = {
    id: `u-mock-${++_seq}`,
    display_name: String(nombre ?? '').trim() || displayNameFromEmail(normalized),
    email: normalized,
    rol,
    estado: 'pendiente',
  }
  _users = [..._users, user]
  return { ...user }
}

export function changeMockUserRole(userId, rol) {
  if (!ROLES.includes(rol)) {
    throw new ApiError('invalid_role', 'El rol indicado no es válido.', 422)
  }
  const actual = _users.find(u => u.id === userId)
  if (!actual) {
    throw new ApiError('user_not_found', 'No se ha encontrado ese usuario.', 404)
  }
  const actualizado = { ...actual, rol }
  _users = _users.map(u => (u.id === userId ? actualizado : u))
  return { ...actualizado }
}

// Sólo para tests: restaura la semilla y el contador entre casos.
export function __resetMockUsers() {
  _users = SEED.map(u => ({ ...u }))
  _seq = 0
}
